import type { AxiosInstance } from "axios";
import type { Store } from "vuex";
import type { RouteLocationNormalized, NavigationGuardNext } from "vue-router";
import axios from "../api/index";
import { resetSessionTimeoutActivity } from "../features/session-timeout";
import { buildAuthRedirectPath } from "../shared/utils/authRedirect";
import * as mutationTypes from "../stores/mutation-types";

interface CurrentDoctor {
  id: number;
  homeUrl?: string;
  capabilities?: string[];
}

interface CapabilityGuardOptions {
  axiosInstance?: AxiosInstance;
  storeInstance: Store<unknown>;
}

export const collectRequiredCapabilities = (matched: RouteLocationNormalized["matched"]): string[] => {
  const collected = matched.flatMap(record => {
    const metaCapabilities = (record.meta.requiredCapabilities || record.meta.requiredcapabilities) as string | string[] | undefined;
    if (!metaCapabilities) return [];
    if (Array.isArray(metaCapabilities)) return metaCapabilities;
    return [metaCapabilities];
  });

  return Array.from(new Set(collected));
};

export const createCapabilityGuard = ({ axiosInstance = axios, storeInstance }: CapabilityGuardOptions) => {
  if (!storeInstance) {
    throw new Error("createCapabilityGuard: storeInstance is required");
  }

  return (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

    if (!requiresAuth) {
      // 公開ルート遷移時も警告状態をクリアしてからログアウト遷移に入る。
      resetSessionTimeoutActivity();
      storeInstance.dispatch("logout");
      next();
      return;
    }

    // ルート遷移をユーザー操作として扱い、タイムアウト監視の基準時刻を更新する。
    resetSessionTimeoutActivity();
    axiosInstance.get("/api/v1/doctors/current_session").then(response => {
      if (response.status === 200 && response.data.id > 0) {
        const currentDoctor: CurrentDoctor = response.data;
        storeInstance.commit(mutationTypes.ASSIGN_CURRENT_DOCTOR, { currentDoctor });
        storeInstance.commit(mutationTypes.ASSIGN_LOGGED_IN, { loggedIn: true });

        const requiredCapabilities = collectRequiredCapabilities(to.matched);
        if (requiredCapabilities.length === 0) {
          next();
          return;
        }

        const hasAllCapabilities = typeof storeInstance.getters.hasAllCapabilities === "function"
          ? storeInstance.getters.hasAllCapabilities(requiredCapabilities)
          : false;

        if (hasAllCapabilities) {
          next();
          return;
        }

        console.log("このページにアクセスする権限がありません。");
        const homeUrl = currentDoctor.homeUrl || "/";
        const targetPath = buildAuthRedirectPath(homeUrl, Boolean(to.query && to.query.__auth));
        next({ path: targetPath, replace: true });
        return;
      }

      // 自動リロード処理が実行されている場合停止する
      clearTimeout(storeInstance.getters.reloadTimeoutId);
      next({
        path: "/login",
        query: {
          redirect: to.fullPath
        }
      });
    }).catch(() => {
      // 自動リロード処理が実行されている場合停止する
      clearTimeout(storeInstance.getters.reloadTimeoutId);
      next({
        path: "/login",
        query: {
          redirect: to.fullPath
        }
      });
    });
  };
};

export { buildAuthRedirectPath };
